import * as React from "react";
import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useApi } from "../../containers/ApiContainer";
import { useAutocompleteResults } from "../../containers/AutocompleteResultsContainer";
import { useLoadingProgress } from "../../containers/LoadingProgressContainer";
import { useUserPreferences } from "../../containers/UserPreferencesContainer";
import { useAircraft } from "./hooks/useAircraft";
import { useAutocompleteTenant } from "./hooks/useAutocompleteTenant";
import { useDimensions } from "./hooks/useDimensions";
import { useGetClickedEntity } from "./hooks/useGetClickedEntity";
import { useGetIndexOfGhostAircraftHoveringOver } from "./hooks/useGetIndexOfGhostAircraftHoveringOver";
import { useIsInLayout } from "./hooks/useIsInLayout";
import { useMovableObstacles } from "./hooks/useMovableObstacles";
import { useOnClick } from "./hooks/useOnClick";
import { useOnContextMenu } from "./hooks/useOnContextMenu";
import { usePolygons } from "./hooks/usePolygons";
import { useOnPositionChange } from "./hooks/useOnPositionChange";
import { useUnselect } from "./hooks/useUnselect";
import { LayoutBackground } from "./LayoutBackground";
import { CollisionOverlay } from "./widgets/CollisionOverlay";
import { Door } from "./widgets/Door";
import { GhostAircraft } from "./widgets/GhostAircraft";
import { Obstacle } from "./widgets/Obstacle";

import {
  AutocompletePosition,
  Entity,
  Hangar,
  Location,
  MovableObstacle,
  Position,
  Preference,
  Ramp,
  Stack,
  Tenant,
} from "../../types";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { DraggingScrollContainer } from "../DraggingScrollContainer/DraggingScrollContainer";

// Keyboard shortcut increments for position fine-tuning
const KEYBOARD_POSITION_INCREMENT_FEET = 1;
const KEYBOARD_ROTATION_INCREMENT_DEGREES = 5;

export type LayoutDisplayOptions = {
  hideUnplaced?: boolean;
  selectable?: boolean;
  readOnly?: boolean;
  printable?: boolean;
  dashColor?: string;
  backgroundColor?: string;
  disableDragProtection?: boolean;
  borderColor?: string;
  hideTailNumbers?: boolean;
  hideNotes?: boolean;
  frozen?: boolean;
  disableGrabScroll?: boolean;
};

export type MoveEntityEvent = {
  direction: string;
  entity: Entity<Tenant | MovableObstacle>;
  previousPosition: Position;
  locationId: string;
};

type Props = {
  stack: Stack;
  setStack: (stack: Stack) => void;
  width?: number;
  height?: number;
  location: Location<Hangar | Ramp>;
  autocomplete?: (tenant: Tenant) => Promise<AutocompletePosition[]>;
  options: LayoutDisplayOptions;
  zoomLevel?: number;
  // Optional callback for scheduled view updates
  onScheduledUpdate?: () => void;
  // Optional scheduled view props
  scheduledViewProps?: {
    currentTime: Date;
    heatmapPlacements?: any[];
    [key: string]: any;
  };
  onIncrementalPark?: (aircraftId: string) => void;
  // Optional overlay content to render inside the layout (e.g., TransformationOverlay)
  overlayContent?: React.ReactNode;
};

export type RightClick = {
  entity_id: string;
  mouseX: number;
  mouseY: number;
};

export const HANGAR_BACKGROUND_COLORS = {
  reference: "#F4D2CD",
  testing: "#23967F",
};

export const Layout: React.FC<Props> = ({
  stack,
  setStack,
  location,
  autocomplete: originalAutocomplete,
  options,
  zoomLevel,
  onScheduledUpdate,
  scheduledViewProps,
  onIncrementalPark,
  overlayContent,
  ...props
}) => {
  const autocomplete =
    originalAutocomplete ?? (async () => [] as AutocompletePosition[]);
  const { setShow: setShowLoadingProgress } = useLoadingProgress();
  const hideUnplaced = options?.hideUnplaced ?? false;
  const readOnly = options?.readOnly ?? false;
  const frozen = options?.frozen ?? false;
  const printable = options?.printable ?? false;
  const spacingBufferRef = React.useRef<HTMLDivElement>();
  const spacingBufferOverlayElement = spacingBufferRef.current;
  const [blockGhostHighlight, setBlockGhostHightlight] =
    React.useState<boolean>(false);

  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const bboxRef = React.useRef<HTMLDivElement>(null);
  const [ghostMouseOverIndex, setGhostMouseOverIndex] =
    React.useState<number>();

  // Type guard to narrow Location<Hangar | Ramp> to Location<Hangar>
  const isHangarLocation = (
    loc: Location<Hangar | Ramp>
  ): loc is Location<Hangar> => {
    return loc.type === "hangar";
  };

  const { loading: loadingUserPreferences, preferences} = useUserPreferences();
  const [rightClick, setRightClick] = React.useState<RightClick>(null);
  const { autocompleteResults, setAutocompleteResults } =
    useAutocompleteResults();
  const { postgrest } = useApi();

  const [deleteConfirmation, setDeleteConfirmation] = React.useState<{
    type: 'aircraft' | 'obstacle';
    tenant?: Tenant;
    obstacle?: MovableObstacle;
  } | null>(null);

  // Store stack in ref to prevent flicker
  const stackRef = React.useRef(stack);
  stackRef.current = stack;  // Direct update during render (safe for refs)

  // Position change handler for keyboard shortcuts
  const onPositionChange = useOnPositionChange(
    { location, stack, setStack },
    [location, stack, setStack]
  );

  // Ref to avoid stale closure in keyboard handler
  const onPositionChangeRef = React.useRef(onPositionChange);
  React.useEffect(() => {
    onPositionChangeRef.current = onPositionChange;
  }, [onPositionChange]);

  const { width, height, feetToPixels } = useDimensions({
    location,
    height: props.height,
    width: props.width,
  });

  const { polygons } = usePolygons(
    {
      stack,
      width,
      feetToPixels,
      isHoldingArea: location.id === "holding-area",
    },
    [width, stack.tenants, stack.movableObstacles, feetToPixels]
  );

  // TODO: make ghost and moving aircraft use hook
  const { polygons: ghostPolygons } = usePolygons(
    {
      stack: {
        ...stack,
        tenants: autocompleteResults.suggestedPositions
          .filter((sp) => sp.hangar_id === location.id)
          .map((position) => ({
            ...autocompleteResults.tenant,
            position: {
              ...autocompleteResults.tenant?.position,
              ...position,
            },
          })),
        movableObstacles: [],
      },
      width,
      feetToPixels,
    },
    [width, autocompleteResults]
  );


  // This hook is responsible for unselecting an aircraft if the user taps
  // anywhere on the page except for a plane or the aircraft list
  useUnselect(
    {
      stack,
      setStack,
    },
    [stack, setStack]
  );

  const isInLayout = useIsInLayout({ location, stack, polygons }, [
    stack.tenants,
    stack.movableObstacles,
    polygons,
  ]);

  const getClickedEntity = useGetClickedEntity(
    {
      bboxRef,
      stack,
      polygons,
      feetToPixels,
    },
    [stack, polygons, feetToPixels]
  );

  const getIndexOfGhostAircraftHoveringOver =
    useGetIndexOfGhostAircraftHoveringOver(
      bboxRef,
      stack,
      ghostPolygons,
      feetToPixels,
      width
    );

  /**
   * Autocomplete the aircraft position
   */
  const autocompleteTenant = useAutocompleteTenant(
    {
      hangarId: location.id,
      enabled: Boolean(originalAutocomplete),
      stack,
      preferences,
      autocomplete: async (tenant: Tenant) => await autocomplete(tenant),
      isInLayout,
      autocompleteResults,
      setAutocompleteResults,
    },
    [stack, preferences, autocompleteResults]
    // Note: autocompleteResults MUST be in deps so the callback can see updated state
    // and early-return (lines 54-61 in useAutocompleteTenant.ts) to prevent infinite loops
  );

  // Store latest autocompleteTenant in a ref to prevent infinite loops
  const autocompleteTenantRef = React.useRef(autocompleteTenant);
  React.useEffect(() => {
    autocompleteTenantRef.current = autocompleteTenant;
  }, [autocompleteTenant]);

  // Run autocomplete when an aircraft is selected (only when selection actually changes)
  const selectedTenantIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const selectedTenant = stack.tenants.find((t) => t.selected);
    const selectedId = selectedTenant?.id || null;

    // Only run autocomplete if the selected tenant ID actually changed
    // (prevents infinite loop when stack.tenants array is recreated with same content)
    if (selectedTenantIdRef.current === selectedId) {
      return;
    }

    selectedTenantIdRef.current = selectedId;

    if (selectedTenant) {
      autocompleteTenantRef.current(selectedTenant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stack.tenants]);
  // Note: autocompleteTenant intentionally NOT in deps - using ref to prevent infinite loop
  // (autocompleteResults changes → autocompleteTenant recreated → would trigger this effect again)

  // Re-run autocomplete when Co-Pilot is enabled while an aircraft is selected
  const prevEnableCoPilotRef = React.useRef(preferences?.enableCoPilot);
  React.useEffect(() => {
    const wasEnabled = prevEnableCoPilotRef.current;
    const isEnabled = preferences?.enableCoPilot;
    prevEnableCoPilotRef.current = isEnabled;

    // Only trigger when toggled from OFF to ON
    if (!wasEnabled && isEnabled) {
      const selectedTenant = stack.tenants.find((t) => t.selected);
      if (selectedTenant) {
        autocompleteTenantRef.current(selectedTenant);
      }
    }
  }, [preferences?.enableCoPilot, stack.tenants]);

  // Keyboard shortcuts: Delete and Arrow keys
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }
      if (readOnly || frozen) return;

      const currentStack = stackRef.current;

      // Delete/Backspace: Delete selected entity
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const selectedTenant = currentStack.tenants.find(t => t.selected);
        if (selectedTenant) {
          e.preventDefault();
          setDeleteConfirmation({ type: 'aircraft', tenant: selectedTenant });
          return;
        }

        const selectedObstacle = currentStack.movableObstacles.find(mo => mo.selected);
        if (selectedObstacle) {
          e.preventDefault();
          setDeleteConfirmation({ type: 'obstacle', obstacle: selectedObstacle });
        }
        return;
      }

      // Arrow keys: Move or rotate selected entity
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (arrowKeys.includes(e.key)) {
        // Find selected entity (tenant or movable obstacle)
        const selectedTenant = currentStack.tenants.find(t => t.selected);
        const selectedObstacle = currentStack.movableObstacles.find(mo => mo.selected);
        const selectedEntity = selectedTenant || selectedObstacle;

        if (!selectedEntity || !selectedEntity.position) return;

        const currentPosition = selectedEntity.position;
        let newX = currentPosition.x;
        let newY = currentPosition.y;
        let newAngle = currentPosition.angle;

        if (e.shiftKey) {
          // Shift + Arrow = Rotation (Left/Right only)
          if (e.key === 'ArrowLeft') {
            newAngle = (currentPosition.angle - KEYBOARD_ROTATION_INCREMENT_DEGREES + 360) % 360;
          } else if (e.key === 'ArrowRight') {
            newAngle = (currentPosition.angle + KEYBOARD_ROTATION_INCREMENT_DEGREES) % 360;
          } else {
            // Shift+Up/Down: no action, allow default behavior
            return;
          }
        } else {
          // Arrow keys = Position movement
          // Note: Y increases upward in logical coordinates (visual up = +Y)
          if (e.key === 'ArrowUp') newY = currentPosition.y + KEYBOARD_POSITION_INCREMENT_FEET;
          if (e.key === 'ArrowDown') newY = currentPosition.y - KEYBOARD_POSITION_INCREMENT_FEET;
          if (e.key === 'ArrowRight') newX = currentPosition.x + KEYBOARD_POSITION_INCREMENT_FEET;
          if (e.key === 'ArrowLeft') newX = currentPosition.x - KEYBOARD_POSITION_INCREMENT_FEET;
        }

        // Only update if position actually changed
        if (newX !== currentPosition.x || newY !== currentPosition.y || newAngle !== currentPosition.angle) {
          e.preventDefault(); // Prevent page scroll only when we're handling the key
          const newPosition: Position = {
            ...currentPosition,
            x: newX,
            y: newY,
            angle: newAngle,
          };
          onPositionChangeRef.current(selectedEntity.entity_id, newPosition);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [readOnly, frozen]);

  const handleConfirmDelete = React.useCallback(async () => {
    if (!deleteConfirmation) return;
    const currentStack = stackRef.current;  // Read from ref, not closure

    if (deleteConfirmation.type === 'aircraft' && deleteConfirmation.tenant) {
      const tenant = deleteConfirmation.tenant;
      setStack({
        ...currentStack,
        tenants: currentStack.tenants.filter(t => t.placement_id !== tenant.placement_id),
      });
      if (currentStack.name === 'reference') {
        await postgrest
          .from('tenant')
          .update({ type: 'transient' })
          .eq('id', tenant.id);
      }

      // Trigger refresh for scheduled view
      if (onScheduledUpdate) {
        onScheduledUpdate();
      }
    } else if (deleteConfirmation.type === 'obstacle' && deleteConfirmation.obstacle) {
      setStack({
        ...currentStack,
        movableObstacles: currentStack.movableObstacles.filter(
          mo => mo.entity_id !== deleteConfirmation.obstacle.entity_id
        ),
      });

      // Trigger refresh for scheduled view
      if (onScheduledUpdate) {
        onScheduledUpdate();
      }
    }
    setDeleteConfirmation(null);
  }, [deleteConfirmation, setStack, postgrest, onScheduledUpdate]);
  // Note: stack removed from dependencies - using stackRef prevents callback recreation on every drag

  const onClick = useOnClick(
    {
      location,
      stack,
      setStack,
      autocompleteResults,
      setAutocompleteResults,
      rightClick,
      getClickedEntity,
      getIndexOfGhostAircraftHoveringOver,
    },
    [stack, setStack, ghostPolygons, autocompleteResults, rightClick, getClickedEntity, getIndexOfGhostAircraftHoveringOver]
  );

  const onMouseMove = React.useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (blockGhostHighlight) {
        return;
      }
      const index = getIndexOfGhostAircraftHoveringOver(evt);
      setGhostMouseOverIndex(index);
    },
    [stack, ghostPolygons, getIndexOfGhostAircraftHoveringOver, blockGhostHighlight]
  );

  const onContextMenu = useOnContextMenu(
    {
      stack,
      getClickedEntity,
      setRightClick,
    },
    [stack, getClickedEntity]
  );
  const Aircraft = useAircraft(
    {
      location,
      stack,
      setStack,
      hideUnplaced,
      isInLayout,
      preferences,
      printable,
      rightClick,
      setRightClick,
      readOnly,
      frozen,
      width,
      height,
      feetToPixels,
      spacingBufferOverlayElement,
      options,
      onScheduledUpdate,
      scheduledViewProps,
      onIncrementalPark,
    },
    [
      location,
      stack,
      setStack,
      readOnly,
      height,
      feetToPixels,
      preferences,
      rightClick,
      setRightClick,
      spacingBufferOverlayElement,
      options,
    ]
  );

  const MovableObstacles = useMovableObstacles(
    {
      location,
      stack,
      setStack,
      preferences,
      rightClick,
      setRightClick,
      readOnly,
      height,
      feetToPixels,
      spacingBufferOverlayElement,
      options,
    },
    [
      stack,
      setStack,
      readOnly,
      height,
      feetToPixels,
      preferences,
      rightClick,
      setRightClick,
      spacingBufferOverlayElement,
      options,
    ]
  );

  React.useEffect(() => {
    setShowLoadingProgress(autocompleteResults?.loading);
  }, [autocompleteResults?.loading]);

  React.useEffect(() => {
    return () => {
      // Clear autocomplete results and hide loading progress when unmounting
      setAutocompleteResults({
        id: null,
        hash: null,
        loading: false,
        tenant: null,
        orientation: null,
        suggestedPositions: [],
      });
      setShowLoadingProgress(false);
    };
  }, []);

  // if a locked aircraft is moved outside the hangar, then unlock it
  React.useEffect(() => {
    for (const tenant of stack.tenants) {
      if (tenant.position.preferences.includes(Preference.LOCKED)) {
        if (!isInLayout(tenant)) {
          setStack({
            ...stack,
            tenants: stack.tenants.map((t) => {
              if (t.id === tenant.id) {
                return {
                  ...t,
                  position: {
                    ...t.position,
                    preferences: t.position.preferences.filter(
                      (p) => p !== Preference.LOCKED
                    ),
                  },
                };
              }
              return t;
            }),
          });
        }
      }
    }
  }, [stack.tenants]);

  const onMouseDown = () => setBlockGhostHightlight(true);
  const onMouseUp = () => setBlockGhostHightlight(false);

  // wait for user prefs...
  if (loadingUserPreferences) {
    return <div />;
  }

  const backgroundColor =
    options?.backgroundColor ??
    (stack.isReference
      ? HANGAR_BACKGROUND_COLORS.reference
      : isDarkMode
        ? theme.palette.background.default
        : "#aab1c7");
  const dashColor =
    options?.dashColor ??
    (stack.isReference
      ? "#f8f8f8"
      : isDarkMode
        ? "#5a6775"
        : "#5474a4");
  const borderColor = options?.borderColor ??
    (isDarkMode ? theme.palette.divider : undefined);

  const autocompleteResultsForThisLayout = {
    ...autocompleteResults,
    suggestedPositions: autocompleteResults.suggestedPositions.filter(
      (sp) => sp.hangar_id === location.id
    ),
  };

  return (
    <DraggingScrollContainer
      disabled={readOnly || options?.disableGrabScroll}
      feetToPixels={feetToPixels}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onClick={onClick}
      onContextMenu={onContextMenu}
    >
      <div
        data-location-id={location.id}
        data-layout={stack.id}
        style={{ display: "flex" }}
      >
        <LayoutBackground
          ref={bboxRef}
          location={location}
          preferences={preferences}
          selectable={false}
          width={width}
          height={height}
          feetToPixels={feetToPixels}
          zoomLevel={zoomLevel}
          backgroundColor={backgroundColor}
          dashColor={dashColor}
          borderColor={borderColor}
          printable={printable}
          onClick={onClick}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onMouseMove={onMouseMove}
          onMouseEnter={() => null}
          onContextMenu={onContextMenu}
          heatmapPlacements={scheduledViewProps?.heatmapPlacements}
          heatmapSelectedDate={scheduledViewProps?.currentTime}
        >
          {!readOnly && preferences.showOverlaps && (
            <CollisionOverlay location={location} polygons={polygons ?? []} />
          )}
          {location.type === "hangar" &&
            Boolean((location as Hangar).left_door) && (
              <Door
                feetToPixels={feetToPixels}
                position="left"
                width={(location as Hangar).left_door}
              />
            )}
          {location.type === "hangar" &&
            Boolean((location as Hangar).right_door) && (
              <Door
                feetToPixels={feetToPixels}
                position="right"
                width={(location as Hangar).right_door}
              />
            )}

          {isHangarLocation(location) &&
            location.obstacles.map((obstacle) => (
              <React.Fragment key={`obstacle-${obstacle.id}`}>
                <Obstacle {...obstacle} feetToPixels={feetToPixels} />
              </React.Fragment>
            ))}
          {isHangarLocation(location) &&
            location.garageDoors.map((garageDoor) => (
              <React.Fragment key={`garage-door-${garageDoor.id}`}>
                <Door
                  type="garage"
                  feetToPixels={feetToPixels}
                  position={garageDoor.wall}
                  offset={
                    garageDoor.wall === "back" ? garageDoor.x : garageDoor.y
                  }
                  width={garageDoor.width}
                />
              </React.Fragment>
            ))}
          <div ref={spacingBufferRef} />
          {/**
           * aircraft is a memo so that it doesn't update during a drag event. this is because we only store state
           * at the end of a drag event
           */}
          {Aircraft}
          {MovableObstacles}
          {Boolean(preferences?.enableCoPilot) && (
            <GhostAircraft
              location={location}
              stack={stack}
              polygons={polygons}
              autocompleteResults={autocompleteResultsForThisLayout}
              mouseOverIndex={ghostMouseOverIndex}
              feetToPixels={feetToPixels}
              height={height}
            />
          )}
          {/* Custom overlay content (e.g., TransformationOverlay for development testing) */}
          {overlayContent}
        </LayoutBackground>
      </div>
      {deleteConfirmation && (
        <ConfirmationDialog
          title={t('widgets.layout.deleteConfirmation.title')}
          text={
            deleteConfirmation.type === 'aircraft'
              ? stack.name === 'reference'
                ? t('widgets.layout.deleteConfirmation.removeTransient')
                : t('widgets.layout.deleteConfirmation.removeAircraft', { locationType: location.type })
              : t('widgets.layout.deleteConfirmation.removeObstacle')
          }
          okText={deleteConfirmation.type === 'aircraft' ? t('widgets.layout.deleteConfirmation.confirmAircraft') : t('widgets.layout.deleteConfirmation.confirmObstacle')}
          onOk={handleConfirmDelete}
          onClose={() => setDeleteConfirmation(null)}
          onCancel={() => setDeleteConfirmation(null)}
        />
      )}
    </DraggingScrollContainer>
  );
};
